import { AriaButtonProps, useButton } from '@react-aria/button';
import { useLink } from '@react-aria/link';
import { AriaLinkProps } from '@react-types/link';
import { ComponentType, ElementType, Ref, RefObject, SVGProps, forwardRef } from 'react';
import { PolymorphicComponentProps } from 'react-polymorphic-box';

import { ButtonBase } from './Button';

type ButtonOwnProps = {
  to?: string;
  tag?: ElementType;
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
  iconPosition?: 'start' | 'end';
};

type BaseButtonProps<E extends React.ElementType> = PolymorphicComponentProps<E, ButtonOwnProps>;

// Returns anchor element styled as a paginator button
// Taking in standard anchor link props, in addition to ButtonOwnProps
export const PaginatorLinkButton = forwardRef(
  ({ children, ...props }: BaseButtonProps<'a'>, innerRef: Ref<HTMLAnchorElement>) => {
    const { linkProps } = useLink(props as AriaLinkProps, innerRef as RefObject<HTMLAnchorElement>);

    return (
      <ButtonBase<'a'>
        {...props}
        {...linkProps}
        ref={innerRef}
        tag="a"
        variant="pagination"
        padding="pagination"
        gap={props.icon ? 'small' : 'none'}
      >
        {children}
      </ButtonBase>
    );
  },
) as <E extends React.ElementType>(props: BaseButtonProps<E>) => React.JSX.Element;

// Returns button element styled as a paginator button
// Taking in standard button props, in addition to ButtonOwnProps
export const PaginatorButton = forwardRef(
  ({ children, ...props }: Omit<BaseButtonProps<'button'>, 'type'>, innerRef: Ref<HTMLButtonElement>) => {
    const { buttonProps } = useButton(
      { ...(props as AriaButtonProps), isDisabled: props.disabled },
      innerRef as RefObject<HTMLButtonElement>,
    );

    return (
      <ButtonBase<'button'>
        {...props}
        {...buttonProps}
        ref={innerRef}
        variant="pagination"
        padding="pagination"
        gap={props.icon ? 'small' : 'none'}
      >
        {children}
      </ButtonBase>
    );
  },
) as <E extends React.ElementType>(props: BaseButtonProps<E>) => React.JSX.Element;
